import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import CoursesScreen from "../../components/screens/Courses/CoursesScreen";
import {
  courses as courseQuerys,
  completions as completionQuerys,
} from "@nualang/nualang-api-and-queries/Queries";
import NoCoursesImage from "../../img/noCourses.svg";
import MembersImage from "../../img/members.svg";
import { getCourseSections } from "@nualang/nualang-api-and-queries/APIs/CourseSections";
import {
  getCourseMember,
  getCourseMembers,
  createCourseMember,
  deleteCourseMember,
} from "@nualang/nualang-api-and-queries/APIs/CourseMembers";
import {
  getCourses,
  copyCourse,
  deleteCourse as deleteCourseAPI,
} from "@nualang/nualang-api-and-queries/APIs/Courses";
import { getMemberCompletions } from "@nualang/nualang-api-and-queries/APIs/Completions";
import {
  courses as courseHelpers,
  completions as completionHelpers,
  users as usersHelpers,
} from "../../utils/helpers";
import config from "../../config";
import { useApp, useCourse } from "../../hooks";

function Courses(props) {
  const {
    authData: { username, attributes = {} },
    handleSignIn,
    navigate,
    isCommunityAccessRestricted,
  } = props;
  const { email } = attributes;
  const queryClient = useQueryClient();
  const { openSnackbar, startLoading, stopLoading } = useApp();
  const { setNewCourse, setIsJoinCourseDialogOpen } = useCourse();

  const { t } = useTranslation();

  const deleteCourseMutation = courseQuerys.useDeleteCourse(deleteCourseAPI, {
    onSettled: () => {
      // Error or success... doesn't matter!
      stopLoading();
    },
    onSuccess: () => {
      openSnackbar("course_deleted", "success");
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.list({
          createdBy: username,
          verbose: false,
          limit: config.limits.courses,
        }),
      });
    },
    onError: () => {
      openSnackbar("problem_deleting", "error");
    },
  });

  const duplicateCourseMutation = courseQuerys.useDuplicateCourse(copyCourse, {
    onSettled: () => {
      // Error or success... doesn't matter!
      stopLoading();
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.list({
          createdBy: username,
          verbose: false,
          // Get all using high limit
          limit: config.limits.courses,
        }),
      });
      if (data && data.Item && data.Item.courseId) {
        const { courseId } = data.Item;
        if (courseId) {
          stopLoading();
          openSnackbar("created", "success");
          navigate(`/courses/${courseId}`);
        }
      }
    },
    onError: () => {
      openSnackbar("problem", "error");
    },
  });

  const learnCoursesQuery = courseQuerys.useCourses(
    async (filters) => {
      const courses = await getCourses(filters);
      const formattedCourses = await courseHelpers.formatCourses(
        courses.Items,
        props.authData,
      );
      return formattedCourses;
    },
    {
      filters: {
        memberId: username,
        verbose: false,
        limit: config.limits.courses,
      },
    },
    { enabled: !!username },
  );

  const teachCoursesQuery = courseQuerys.useCourses(
    async (filters) => {
      const courses = await getCourses(filters);
      const formattedCourses = await courseHelpers.formatCourses(
        courses.Items,
        props.authData,
      );
      return formattedCourses;
    },
    {
      filters: {
        createdBy: username,
        verbose: false,
        // Get all using high limit
        limit: config.limits.courses,
      },
    },
    { enabled: !!username },
  );

  const memberCompletionsQuery = completionQuerys.useMemberCompletions(
    async (memberId) => {
      const memberCompletions = await getMemberCompletions(memberId);
      const formattedCompletions = await completionHelpers.formatCompletions(
        memberCompletions.Items,
        memberId,
        attributes.preferred_username,
      );
      return formattedCompletions;
    },
    {
      memberId: username,
    },
    { enabled: !!username },
  );

  // Mutations
  const joinCourseMutation = courseQuerys.useJoinCourse(createCourseMember, {
    onSettled: () => {
      // Error or success... doesn't matter!
      stopLoading();
    },
    onSuccess: async (variables) => {
      openSnackbar("joined_course", "success");
      navigate(`/courses/${variables.courseId}`);
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMembers(
          variables.courseId,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMember(
          variables.courseId,
          username,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.list({
          memberId: username,
          verbose: false,
        }),
      });
    },
    onError: () => {
      openSnackbar("problem", "error");
    },
  });
  const leaveCourseMutation = courseQuerys.useLeaveCourse(deleteCourseMember, {
    onSettled: () => {
      // Error or success... doesn't matter!
      stopLoading();
    },
    onSuccess: async (variables) => {
      openSnackbar("left_course", "success");
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMembers(
          variables.courseId,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.itemMember(
          variables.courseId,
          username,
          username,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: courseQuerys.courseKeys.list({
          memberId: username,
          verbose: false,
        }),
      });
    },
    onError: () => {
      openSnackbar("problem", "error");
    },
  });

  const handleStartCourse = async (course) => {
    if (!username) {
      handleSignIn();
    } else {
      setNewCourse(course);
      if (course.visibility === "private") {
        setIsJoinCourseDialogOpen(true);
      } else {
        startLoading();
        joinCourseMutation.mutate({ courseId: course.courseId, username });
      }
    }
  };

  const handleDeleteCourse = (courseId) => {
    startLoading();
    deleteCourseMutation.mutate({ courseId, username });
  };

  const handleLeaveCourse = (courseId) => {
    startLoading();
    leaveCourseMutation.mutate({ courseId, username });
  };

  const handleViewCourse = (courseId) => {
    navigate(`/courses/${courseId}`);
  };

  const handleViewCourseTopic = (cId, sectionId, topicId) => {
    navigate(`/courses/${cId}/${sectionId}/${topicId}`);
  };

  const handleSearch = isCommunityAccessRestricted ? undefined : () => navigate("/search");
  const handleCreateCourse = () => navigate("/courses/create");

  const handleViewActivity = (memberId) => {
    navigate(`/activity/member/${memberId}`);
  };

  const getMemberDetails = (members) => usersHelpers.fetchMembersDetails(members);

  const handleViewUserProfile = (profileId) => {
    navigate(`/profiles/${profileId}`);
  };

  const handleDuplicateCourse = async (values) => {
    startLoading();
    await duplicateCourseMutation.mutate({ values });
    // stopLoading();
  };

  return (
    <>
      <CoursesScreen
        t={t}
        role={attributes["custom:role"]}
        isLoading={learnCoursesQuery.isLoading || teachCoursesQuery.isLoading}
        placeholderImageUrl={NoCoursesImage}
        memberPlaceholderImageUrl={MembersImage}
        learnCourses={
          learnCoursesQuery.isSuccess &&
          Array.isArray(learnCoursesQuery.data) &&
          learnCoursesQuery.data.length
            ? learnCoursesQuery.data
            : []
        }
        teachCourses={
          teachCoursesQuery.isSuccess &&
          Array.isArray(teachCoursesQuery.data) &&
          teachCoursesQuery.data.length
            ? teachCoursesQuery.data
            : []
        }
        completions={
          memberCompletionsQuery.isSuccess &&
          Array.isArray(memberCompletionsQuery.data) &&
          memberCompletionsQuery.data.length
            ? memberCompletionsQuery.data
            : []
        }
        handleViewCourseTopic={handleViewCourseTopic}
        handleStartCourse={handleStartCourse}
        handleLeaveCourse={handleLeaveCourse}
        handleViewCourse={handleViewCourse}
        handleSearch={handleSearch}
        handleCreateCourse={handleCreateCourse}
        getMemberDetails={getMemberDetails}
        getCourseSections={getCourseSections}
        memberId={username}
        getCourseMember={getCourseMember}
        getCourseMembers={getCourseMembers}
        username={username}
        membersProps={{
          handleViewActivity,
        }}
        handleViewUserProfile={handleViewUserProfile}
        handleDuplicateCourse={handleDuplicateCourse}
        email={email}
        isTeacher={attributes["custom:role"] === "teach"}
        handleDeleteCourse={handleDeleteCourse}
      />
    </>
  );
}

Courses.propTypes = {
  authData: PropTypes.object,
  navigate: PropTypes.func,
  isCommunityAccessRestricted: PropTypes.bool,
};

export default Courses;
